import { Component, computed, inject, signal, afterNextRender } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { CategoryFaqs } from '../../shared/types/common.types';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ResolutionService } from '../../shared/resolution.service';
import { CommonModule } from '@angular/common';
import { SeoService } from '../../shared/seo.service';
import { Pages } from '../../shared/types/common.enums';
import { SEO_PAGES_CONFIG } from '../../shared/header/header.lists';

@Component({
  selector: 'app-faq',
  standalone: true,
  imports: [RouterLink, NgbAccordionModule, CommonModule],
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.scss'
})
export class FaqComponent {

  //#region Services

  #sanitizer = inject(DomSanitizer);

  #resolutionService = inject(ResolutionService);

  #seoService = inject(SeoService);

  //#endregion

  //#region Properties

  categories = signal<CategoryFaqs[]>([]);

  questions = computed(() => this.categories().find(x => x.active));

  isMobile = computed(() => this.#resolutionService.isMobile());

  //#endregion

  //#region Constructor

  constructor() {
    this.#seoService.setUpSEOTags(SEO_PAGES_CONFIG[Pages.faq]);
    afterNextRender(() => this.#load())
  }

  //#endregion

  //#region Methods

  #load() {

    this.categories.set([
      {
        active: true,
        category: 'How Does it Work',
        questions: [
          {
            question: 'How does it work?',
            answer: this.#sanitizeHtml(`
              <ul class="text-start">
                <li><p>Sign up <a [routerLink]="['/signup']" class="fw-semibold">here</a> to create a Carlton Farms account. Enter a delivery address to get your farm fresh goods delivered to your door or office building. We also have several drop-off or mobile market locations around the metro Atlanta area. Though we ask you to input your credit card information, registration is free, and you won't be charged until your first order is delivered.</p></li>
                <li><p>After you have signed up, it's time to start shopping! We carry a large selection of fresh farm foods for you to choose from, including pasture-raised meats, wild-caught fish, raw dairy products, pastured eggs, organic fruits and vegetables, all-natural pantry products, cultured products, raw honey & personal care, all from local sources. Orders can be placed for individual orders or a subscription order.</p></li>
                <li><p>Your account will be charged the evening before delivery.</p></li>
              </ul>
            `)
          },
          {
            question: 'What delivery options do you offer?',
            answer: this.#sanitizeHtml(`
              <p class="fw-bold">Home Delivery</p>
              <p class="m-0">
                  We offer home deliveries every other week. Deliveries can be made to homes, offices or apartments. We proudly serve
                  the metro Atlanta area and its wonderful surrounding communities.
              </p>
              <p>Deliveries are made Tuesday through Saturday between 9 and 6 pm.</p>
              <p>Your delivery day is assigned to you based on your location.</p>

              <p class="fw-bold">Popup Pickup</p>
              <p>Our Pop-Up Pick-Ups are held at a specific location on a certain day and time</p>

              <p class="fw-medium fst-italic m-0">Tuesday</p>
              <p class="m-0">Marietta – 10:00 - 11:00 am – Mt. Paran North Church of God – 1700 Allgood Rd, Marietta, GA 30062</p>
              <p>Woodstock – 12:00 - 1:30 pm – Breadbeckers – 296 Bell Park Drive, Woodstock, GA 30188</p>

              <p class="fw-medium fst-italic m-0">Wednesday</p>
              <p class="m-0">Dunwoody – 10:30 am - 12:00 pm – The Terraces – 115 Perimeter Center Place, Dunwoody, GA 30346</p>
              <p>Buford – 1:00 – 2:00 pm – Covenant Presbyterian Church – 1420 Rock Springs Rd, Buford, GA 30519</p>

              <p class="fw-medium fst-italic m-0">Thursday</p>
              <p class="m-0">Alpharetta/Roswell – 10:30 -11:30 am – St Mary of Egypt Orthodox Church - 1765 Woodstock Rd., Roswell, GA
                  30075</p>
              <p>Cumming – 2:00 - 3:00 pm – Chalcedon Presbyterian Church –302 Pilgrim Mill Rd, Cumming, GA 30040</p>

              <p class="fw-medium fst-italic m-0">Friday</p>
              <p class="m-0">Dallas / Powder Springs - 10:00 -11:00 am -West Ridge Church - 3522 Hiram Acworth Hwy., Dallas, GA 30157
              </p>
              <p>
                  East Atlanta Village - 12:00-1:00 pm - Village Church - 1231 Glenwood Ave SE, Atlanta, GA 30316 (located behind
                  Village Fitness, the old ACE Hardware Building)
              </p>

              <p class="fw-medium fst-italic m-0">Saturday</p>
              <p>Rome- every other Saturday 9:30-10:30 am -Bridgepoint Plaza, Broad St., Rome, GA 30161 Link</p>

              <p class="fw-bold">Drop-off</p>
              <p>
                  We’ll deliver your pre-ordered items for you to pick up during the designated times. You’ll have plenty of time to
                  collect your order.
              </p>
              <p class="ps-5 fw-semibold m-0">
                  Kennesaw
              </p>
              <p class="ps-5 m-0">Exodus Health Center - 2750 Jiles Rd. NW, Kennesaw, GA 30144</p>
              <p class="ps-5">Pick-Up Times: Wednesday 10-11 am & 3-6 pm</p>
            `)
          },
          {
            question: 'When is the order deadline?',
            answer: this.#sanitizeHtml('<p>Orders must be placed by 9 am the day before your delivery. We apologize, but we cannot take late orders.</p>')
          },
          {
            question: 'What if I’m not home for my Home Delivery?',
            answer: this.#sanitizeHtml('<p>Our driver will place your order by your front door, orders will be left in the cooler with ice that you provide.</p>')
          },
        ]
      },
      {
        active: false,
        category: 'Personal Information',
        questions: [
          {
            question: 'Am I obligated to purchase?',
            answer: this.#sanitizeHtml('<p>Signing up is free & there are no obligations to purchase.</p>')
          },
          {
            question: 'Is my information safe?',
            answer: this.#sanitizeHtml('<p>We will never share or sell your information.</p>')
          },
          {
            question: 'Payments',
            isHeaderSection: true
          },
          {
            question: 'How do I know my debit/credit card information is secure on your website?',
            answer: this.#sanitizeHtml('<p>Your privacy and payment security is incredibly important to us. For this reason, we have invested in a secure credit card processing system through Authorize. As a PCI compliant service provider, Authorize ensures our customer’s credit card information is safe, secure and properly handled during the billing process.</p>')
          },
          {
            question: 'How do I pay?',
            answer: this.#sanitizeHtml('<p>When you setup your account, you entered your credit card information. You will be charged the evening before delivery. We only accept credit or debit card for payment.</p>')
          },
          {
            question: 'Orders',
            isHeaderSection: true
          },
          {
            question: 'Can I setup a subscription order?',
            answer: this.#sanitizeHtml('<p>Yes. This option is very convenient for ordering the same items, you can choose from weekly, bi-weekly, or monthly. Changes can be made until 9 am the day before your next delivery.  Order changes can be made online by logging into your account</p>')
          },
          {
            question: 'I am going on vacation, can I suspend my delivery when I am gone?',
            answer: this.#sanitizeHtml('<p>Yes. Deliveries may be suspended while you are on vacation. Click on the skip delivery tab to enter your vacation dates.  Nine am the day before the delivery is the deadline for changes.</p>')
          },
          {
            question: 'What happens when I miss my pick up?',
            answer: this.#sanitizeHtml(`
              <p class="fw-semibold m-0">Popup Pickups</p>
              <p class="m-0">
                  Popup Pickups Your account will be credited for the order minus the delivery fee & any fruits or vegetables you
                  ordered.
              </p>
              <p>We reserve the right to charge a 15% restocking fee for missed pickups or product shortages.</p>

              <p class="fw-semibold m-0">Drop-offs</p>
              <p class="m-0">
                  Please contact the drop-off location to make arrangements for you to pick up if you missed getting your order during
                  their pickup window.
              </p>
              <p>
                  If your order is still there when we drop off the following week your order will be returned to the farm & you will
                  forfeit your order.
              </p>  
            `)
          },
          {
            question: 'Do you charge a restocking fee for missed pickups?',
            answer: this.#sanitizeHtml(`
              <p class="m-0">We reserve the right to charge a 15% restocking fee for missed pickups or product shortages.</p>  
              <p>*Fruits & vegetables are unable to be restocked.</p>
            `)
          },
          {
            question: 'Subscription Products',
            isHeaderSection: true
          },
          {
            question: 'Do I need to have a subscription?',
            answer: this.#sanitizeHtml('<p>Feel free to order whenever you need something! Plus, if you find yourself needing certain items more often, you can easily set up a subscription to make things even more convenient.</p>')
          },
          {
            question: 'What frequency options are available?',
            answer: this.#sanitizeHtml('<p>You can choose from weekly, every other week or monthly.</p>')
          },
        ]
      },
      {
        active: false,
        category: 'Delivery',
        questions: [
          {
            question: 'Should I leave out a cooler & ice?',
            answer: this.#sanitizeHtml(`<p>Please have a cooler out for us large enough to contain your order and don't forget the ice.If you don't have a cooler or forget to leave one out we will deliver your order in an insulated bag with ice. We will add a small fee to your account for the insulated bag.</p>`)
          },
          {
            question: 'Do you deliver to apartments?',
            answer: this.#sanitizeHtml(`<p>Yes! Give us a call or put in the notes any information needed like a gate code, and we'll make sure your items are delivered safely.</p>`)
          },
          {
            question: 'How often do you deliver to homes?',
            answer: this.#sanitizeHtml(`<p>Home deliveries are every other week.</p>`)
          },
          {
            question: 'What is the Home Delivery fee?',
            answer: this.#sanitizeHtml(`
              <p class="fw-semibold">minimum order is $100</p>
              <table class="table">
                <thead>
                  <tr>
                    <th scope="col">Order Amount</th>
                    <th scope="col">Delivery Fee</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>From $100 to $200</td>
                    <td>$15</td>
                  </tr>
                  <tr>
                    <td>Orders over $200</td>
                    <td>$10</td>
                  </tr>
                </tbody>
              </table>
            `)
          },
          {
            question: 'Do I have to be home to accept delivery?',
            answer: this.#sanitizeHtml(`<p>No. Our driver will place your order by your front door, orders will be left in the cooler with ice that you provide. If you don't have a cooler or forget to leave one out we will deliver your order in an insulated bag with ice. We will add a small fee to your account for the insulated bag.</p>`)
          }
        ]
      },
      {
        active: false,
        category: 'Tell Me More About Your Products',
        questions: [
          {
            question: 'Tell me more about the meat you sell:',
            answer: this.#sanitizeHtml(`
              <p class="m-0 fw-semibold">Grass-fed Beef:</p>
              <p>Our beef cattle roam our pastures 365 days per year. The diet consists of grass... no grains at all. We grow forage made into baled silage "baleage" from Ryegrass, Sudan grass, Millet & Oat grass to get through the winter months, which receives no pesticides or herbicides.</p>
            `),
            isHeaderSection: true,
          },
          {
            question: 'Do you use growth hormones or antibiotics with your cows?',
            answer: this.#sanitizeHtml(`
              <p>Our cows receive antibiotics only when they are sick and in need of an antibiotic. In that case, they are NOT used until they are deemed healthy and antibiotic free. We never give our cows antibiotics as a preventative method, nor do they ever receive growth hormones of any kind!</p>
            `)
          },
          {
            question: 'Is your beef totally grass fed?',
            answer: this.#sanitizeHtml(`
              <p>Our beef cows are raised completely on grass with no "grain finishing.”</p>
              
              <p class="m-0 fw-semibold fst-italic">Grass-fed Lamb:</p>
              <p>Our lambs range on pasture with their mothers, grazing on fragrant wild grasses and continuing to drink their mother’s milk (for the first few months), as nature intended.</p>

              <p class="m-0 fw-semibold fst-italic">Pastured Pork:</p>
              <p>Our pigs are on pasture all day, everyday. We make every effort to ensure that they have access to greens and veggies at all times.</p>

              <p class="fw-semibold fst-italic">Pastured Poultry:</p>
              
              <p class="m-0 fw-semibold fst-italic">Broiler (meat) Chickens:</p>
              <p>Our pasture-raised slow-growing meat chickens have the pleasure of roaming on acres of pasture, free to enjoy the sunlight and fresh air as well as a smorgasbord of worms, insects, bugs & grass. They also have access to non-GMO feed, which is sourced locally. If they want shelter, they can get out of the elements in their custom-designed chicken barn. There they can nest, rest and drink fresh water. The chickens are moved every few days to ensure the freshest environment possible. Our pastures never receive pesticides or herbicides of any kind, providing the best-growing conditions for meat chickens bearing the Carlton Farm name.</p>
              
              <p class="m-0 fw-semibold fst-italic">Stewing Hens:</p>
              <p>We like to keep our egg-laying hens for two years so that they can live a full life. They are allowed to molt naturally, and we never withhold feed or use lighting to force molting. However, eventually, their egg-producing days are behind them. When that time comes, we process those birds and make them available for stewing hens. The meat is unsuitable for roasting, but these birds make excellent chicken soup/stock, and their age adds richness to the flavor of the stock.</p>

              <p class="m-0 fw-semibold fst-italic">Turkeys:</p>
              <p>Free Range Turkeys are available during the Thanksgiving season. These turkeys are raised on pasture and fed a non-GMO diet that we source locally. Turkeys are rotated onto fresh pasture every few days so that they are allowed access to plenty of forage and insects.</p>
            `)
          },
          {
            question: 'Tell Me More About the Dairy & Eggs You Sell:',
            isHeaderSection: true
          },
          {
            question: 'How do you feed your dairy cows?',
            answer: this.#sanitizeHtml(`
              <p>Our Jersey dairy cows roam the pastures of Carlton Farms 365 days per year. In addition to the grass that they harvest from the pasture, our cows also eat grass that we have harvested from our farm in the form of hay that has not been sprayed with pesticides or herbicides. To get the girls into the milking stall, we entice them in with a scoop of feed (about 5 pounds per day).</p>  
            `)
          },
          {
            question: 'Are the cows A2?',
            answer: this.#sanitizeHtml(`
              <p>Our Jersey cows have A2 genetics.</p>  
            `)
          },
          {
            question: 'Is your raw milk for pets pastured/homogenized?',
            answer: this.#sanitizeHtml(`
              <p>No, it is raw milk. Nothing is ever added to the milk and nothing is taken away from the milk.</p>  
            `)
          },
          {
            question: 'Why is the milk labeled for pets?',
            answer: this.#sanitizeHtml(`
              <p>It is illegal to sell raw milk in the state of Georgia with the exception of pet milk.</p>  
            `)
          },
          {
            question: 'Why is there no expiration date on your raw milk for pets?',
            answer: this.#sanitizeHtml(`
              <p>The state of Georgia doesn’t require raw pet milk to be dated. With that said, we milk our cows once per day, bottle it and by early the next morning that milk will be out for delivery for you to enjoy.</p>  
            `)
          },
          {
            question: 'How do I store the raw pet milk?',
            answer: this.#sanitizeHtml(`
              <p class="m-0">The ideal for raw pet milk storage is to store milk between 35 and 37 degrees F.</p>
              <p>After you get your milk home:</p>
              <ul>
                <li>Check temp of fridge; should be 37 degrees or below, optimal temperature is 36.</li>
                <li>Store milk in the coldest part of the fridge.</li>
                <li>Never store your milk on the door.</li>
                <li>May need to place ice packs next to milk in fridge if having spoiling problems, especially for the milk that will be stored the longest.</li>
                <li>Promptly return milk to refrigerator after using.</li>
              </ul>
              <p>Milk should still taste good after 7-10 days, but most of my customers can get two weeks if they keep the milk really cold.</p>
            `)
          },
          {
            question: 'Can I freeze the pet milk?',
            answer: this.#sanitizeHtml(`
              <p class="m-0">Yes, it can be frozen.</p>  
              <p>Most people freeze in the jug that the milk comes in as this is easier.  If you do that, take out about a cup of milk before freezing.</p>
              <p class="m-0">Thawing:</p>
              <p class="m-0">The best way to thaw the milk is to place it on the countertop and wrap a towel around it.  You will want to shake it frequently to minimize the small pieces of butterfat.  Those small pieces of butterfat are an unavoidable part of frozen/thawed milk.  You can minimize them but you will never eliminate them.</p>
              <p>Frozen milk is good in the freezer for a couple of months, and once thawed the shelf life will be about the same as normal (10 days give or take).</p>
            `)
          },
          {
            question: 'Do you use growth hormones or antibiotics with your milk cows?',
            answer: this.#sanitizeHtml(`
              <p>Our cows receive antibiotics only when they are sick and in need of an antibiotic. In that case, their milk is taken off the line and NOT used until they are deemed healthy and antibiotic free. We never give our cows antibiotics as a preventative method nor do they ever receive growth hormones of any kind!</p>  
            `)
          },
          {
            question: 'Tell Me More About The Vegetables You Sell:',
            isHeaderSection: true
          },
          {
            question: 'What is your definition of local?',
            answer: this.#sanitizeHtml(`
              <p>All of the vegetable farms are within 100 miles of our delivery area. We depend on our network of farmers to supply us with seasonal, yet diverse produce.</p>  
            `)
          },
          {
            question: 'Are your vegetables organic?',
            answer: this.#sanitizeHtml(`
              <p>All of our produce is organically grown. We strive to choose only organically grown or certified organic produce from as close to home as possible. However, sometimes we are unable to find a local AND organic option, and in that event we may choose to offer a pesticide-free local product from a farmer that uses integrated pest management on their crops.</p>  
            `)
          },
          {
            question: 'Farm Info:',
            isHeaderSection: true
          },
          {
            question: 'Can I visit your farm?',
            answer: this.#sanitizeHtml(`
              <p>Yes! The farm is open Monday-Saturday from 10-6.</p>  
            `)
          },
          {
            question: 'What are the farm store hours?',
            answer: this.#sanitizeHtml(`
              <p>The farm store is open Monday-Saturday from 10-6.</p>  
            `)
          },
          {
            question: 'Can I have a party at your farm?',
            answer: this.#sanitizeHtml(`
              <p class="m-0">Yes! We host birthday parties at the farm.</p>  
              <p>Please call Brad at 770-546-5179 for more information.</p>  
            `)
          },
          {
            question: 'Tell me more about the corn maze',
            answer: this.#sanitizeHtml(`
              <p class="m-0">At Carlton Farms we design and cut our own maze. Our corn maze is a 5 acre field with approximately 2 miles of trails. We cut our design in the maze with lots of dead ends and wrong turns for you to navigate. It usually takes maze goers 30 minutes to an hour to navigate the maze. It is perfect for all ages!</p>
              <p>Visit our corn maze page for more information.</p>
            `)
          },
          {
            question: 'Is the farm wheelchair/stroller accessible?',
            answer: this.#sanitizeHtml(`
              <p>Yes, it is! The corn maze as well as baby animal barn and hayride are all accessible.</p>
            `)
          },
          {
            question: 'Tell me more about school tours',
            answer: this.#sanitizeHtml(`
              <p class="m-0">We offer Spring tours beginning in late March going through May & Fall tours beginning in late September going through Early November.</p>
              <p class="m-0">Visit our school tour page for more information.</p>
              <p>To schedule your trip to the farm, call Brad Carlton at (770)546-5179.</p>
            `)
          },
        ]
      },
    ]);
  }

  categoryChanged(category: CategoryFaqs) {
    this.categories.update(value => {
      value.forEach(x => x.active = x.category === category.category);
      return [...value];
    });
  }

  #sanitizeHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return this.#sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  //#endregion
}
